/**
 * Shared constants. Durations are in milliseconds (`_MS`) or seconds (`_SECONDS`) – the suffix
 * is part of the contract. All timestamps in the database are INTEGER Unix epoch milliseconds
 * (ADR 017).
 */
import { TemplateVersion } from './enums';

const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

// --- Activation codes (§10) -------------------------------------------------------------------

/** §10: reduced alphabet without 0, O, 1, I, L. 31 characters → ~49.5 bit entropy for 10 chars. */
export const ACTIVATION_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const ACTIVATION_CODE_LENGTH = 10;
/** Display grouping `XXXXX-XXXXX`. */
export const ACTIVATION_CODE_GROUP_SIZE = 5;
/** Upper bound for raw user input before normalisation (spaces, dashes). */
export const ACTIVATION_CODE_INPUT_MAX_LENGTH = 32;

// --- Registration / verification (§16–§20) -----------------------------------------------------

/** §17/§18: verification link and pending reservation are valid for 24 h. */
export const VERIFICATION_TOKEN_TTL_MS = 24 * HOUR_MS;
/** §18: a PENDING_EMAIL registration blocks its code for at most this long. */
export const PENDING_REGISTRATION_TTL_MS = VERIFICATION_TOKEN_TTL_MS;
/**
 * §16: lifetime of the registration session cookie. Covers the 24 h pending window plus the
 * provisioning retry window (≈ 7.4 h, see RETRY_BACKOFF_SECONDS) with margin.
 */
export const REGISTRATION_SESSION_TTL_MS = 72 * HOUR_MS;
/** §20: minimum gap between two verification emails of one registration. */
export const RESEND_COOLDOWN_MS = 60 * SECOND_MS;
/** §20 global abuse limit: max. verification emails per registration (start + resends + changes). */
export const VERIFICATION_EMAIL_MAX_PER_REGISTRATION = 6;
/** Max. email corrections per registration (§19). */
export const EMAIL_CHANGE_MAX_PER_REGISTRATION = 3;

// --- Admin auth (ADR 014) ---------------------------------------------------------------------

/** Magic link validity. */
export const ADMIN_LOGIN_LINK_TTL_MS = 15 * MINUTE_MS;
/** Sliding idle timeout of an admin session. */
export const ADMIN_SESSION_IDLE_TTL_MS = 12 * HOUR_MS;
/** Absolute maximum lifetime of an admin session. */
export const ADMIN_SESSION_ABSOLUTE_TTL_MS = 7 * DAY_MS;
/** `last_seen_at` is only written if older than this (saves D1 writes, Free Plan). */
export const ADMIN_SESSION_TOUCH_INTERVAL_MS = 5 * MINUTE_MS;
/** Max. login link requests per email address within ADMIN_LOGIN_LINK_RATE_WINDOW_MS. */
export const ADMIN_LOGIN_LINK_MAX_PER_WINDOW = 5;
export const ADMIN_LOGIN_LINK_RATE_WINDOW_MS = HOUR_MS;

// --- Tokens -----------------------------------------------------------------------------------

/** §17: opaque tokens have at least 256 bit entropy (32 random bytes, base64url). */
export const OPAQUE_TOKEN_BYTES = 32;

// --- Jobs / queues (ADR 016, ADR 019) ---------------------------------------------------------

/**
 * Backoff before retry n (1-based: index 0 = delay before the 1st retry).
 * Sum = 26 760 s ≈ 7.4 h – well inside the 24 h queue retention of the Free Plan.
 */
export const RETRY_BACKOFF_SECONDS = [60, 300, 900, 3600, 21600] as const;
/** Max. number of retries after the first attempt (= wrangler `max_retries`). */
export const MAX_JOB_RETRIES = RETRY_BACKOFF_SECONDS.length;
/** Max. attempts in total (first attempt + retries); default for `integration_jobs.max_attempts`. */
export const MAX_JOB_ATTEMPTS = 1 + MAX_JOB_RETRIES;

/** Delay before retry `retryNumber` (1-based). Clamps to the last value. */
export function retryDelaySeconds(retryNumber: number): number {
  const lastDelay = RETRY_BACKOFF_SECONDS[RETRY_BACKOFF_SECONDS.length - 1] ?? 0;
  const index = Math.min(Math.max(retryNumber, 1), RETRY_BACKOFF_SECONDS.length) - 1;
  return RETRY_BACKOFF_SECONDS[index] ?? lastDelay;
}

/** §108: a registration/job/batch generation stuck longer than this is reported/re-scheduled. */
export const STUCK_THRESHOLD_MS = 30 * MINUTE_MS;
/** Max. rows each maintenance step touches per cron run (10 ms CPU, D1 quotas). */
export const MAINTENANCE_ROWS_PER_RUN = 50;

/** Default codes per GENERATE_BATCH_CODES chunk; override via env `CODE_GENERATION_CHUNK_SIZE`. */
export const CODE_GENERATION_CHUNK_SIZE_DEFAULT = 25;
/** Default max pages per Browser Run render; override via env `PDF_MAX_PAGES_PER_RENDER`. */
export const PDF_MAX_PAGES_PER_RENDER_DEFAULT = 100;
/** At most one Google-Sheet sync per batch within this window (WS4 debouncing). */
export const SHEET_SYNC_DEBOUNCE_MS = 5 * MINUTE_MS;
/**
 * Max. rows (= decrypted codes) per SYNC_BATCH_SHEET job / SheetSyncRequest part (CR-WS1-2,
 * ADR 067): ≈ 250 × 7 µs decrypt keeps one queue invocation far below the 10 ms CPU limit.
 */
export const SHEET_SYNC_ROWS_PER_PART = 250;
/**
 * Max. codes per part of an asynchronous BATCH_CSV export (CR-WS3-2, ADR 066): one queue invocation
 * decrypts and formats at most this many codes (≈ 7 µs AES-GCM per code) – far below 10 ms CPU.
 */
export const CSV_CODES_PER_PART = 250;
/**
 * Max. codes of the synchronous `GET /admin/batches/:id/csv` (one HTTP request decrypts all codes);
 * larger batches must use the asynchronous BATCH_CSV document (ADR 066).
 */
export const CSV_SYNC_MAX_CODES = 250;

// --- Retention (§18, §63) ---------------------------------------------------------------------

/** Default days after which PII of EXPIRED/CANCELLED registrations is purged. Env `PII_RETENTION_DAYS`. */
export const PII_RETENTION_DAYS_DEFAULT = 30;
/** Rows in `webhook_replay_guard` live at least as long as the signature tolerance. */
export const WEBHOOK_REPLAY_GUARD_TTL_MS = 24 * HOUR_MS;

// --- Templates (§45) --------------------------------------------------------------------------

export const DEFAULT_TEMPLATE_VERSION = TemplateVersion.WELCOME_LETTER_V1;

// --- Input limits (§15) -----------------------------------------------------------------------

export const NAME_MAX_LENGTH = 100;
/** RFC 5321 path limit. */
export const EMAIL_MAX_LENGTH = 254;
export const COMPANY_NAME_MAX_LENGTH = 200;
export const BATCH_NAME_MAX_LENGTH = 200;
export const SLUG_MAX_LENGTH = 80;
export const LEARNINGSUITE_GROUP_ID_MAX_LENGTH = 64;
export const DISPLAY_NAME_MAX_LENGTH = 100;
export const REASON_MAX_LENGTH = 500;
export const BATCH_QUANTITY_MIN = 1;
export const BATCH_QUANTITY_MAX = 1000;
export const SEARCH_MAX_LENGTH = 100;
/** Max. request body size accepted by the API (bytes). */
export const REQUEST_BODY_MAX_BYTES = 64 * 1024;

// --- Pagination -------------------------------------------------------------------------------

export const PAGE_SIZE_DEFAULT = 25;
export const PAGE_SIZE_MAX = 100;

// --- Public status polling (Free Plan: 100 000 requests/day) ----------------------------------

export const STATUS_POLL_INITIAL_MS = 3 * SECOND_MS;
export const STATUS_POLL_MAX_MS = 15 * SECOND_MS;
export const STATUS_POLL_GIVE_UP_MS = 3 * MINUTE_MS;
/** Public state switches to PROVISIONING_DELAYED after this many failed attempts. */
export const PROVISIONING_DELAYED_AFTER_ATTEMPTS = 2;

// --- HTTP: headers and cookies ----------------------------------------------------------------

export const API_PREFIX = '/api/v1';
export const REQUEST_ID_HEADER = 'X-Request-Id';
/** Admin CSRF: double submit. Cookie (readable by JS) + header, server compares with session hash. */
export const CSRF_HEADER = 'X-CSRF-Token';
export const CSRF_COOKIE = '__Host-lsc_csrf';
/**
 * Sent by the web client on every API request. Custom headers force a CORS preflight, so
 * cross-site forms cannot forge mutating public requests (in addition to Origin checks, WS2).
 */
export const CLIENT_HEADER = 'X-LSC-Client';
export const CLIENT_HEADER_VALUE = 'web';
/** Turnstile token for GET requests (POST bodies carry `turnstileToken`). */
export const TURNSTILE_TOKEN_HEADER = 'X-Turnstile-Token';
export const REGISTRATION_SESSION_COOKIE = '__Host-lsc_reg';
export const ADMIN_SESSION_COOKIE = '__Host-lsc_admin';

/** Header names for signed n8n ↔ worker calls (§29). Signature = hex(HMAC-SHA256(secret, `${ts}.${rawBody}`)). */
export const SIGNATURE_TIMESTAMP_HEADER = 'X-LSC-Timestamp';
export const SIGNATURE_HEADER = 'X-LSC-Signature';
/** Signature header value prefix, e.g. `v1=<hex>`. */
export const SIGNATURE_VERSION_PREFIX = 'v1=';
export const SIGNATURE_TOLERANCE_SECONDS = 300;

/** Resend webhooks are signed with Svix. */
export const SVIX_ID_HEADER = 'svix-id';
export const SVIX_TIMESTAMP_HEADER = 'svix-timestamp';
export const SVIX_SIGNATURE_HEADER = 'svix-signature';

// --- Routes of the web app (used in emails, QR codes, redirects) ------------------------------

export const WEB_ROUTES = {
  home: '/',
  activation: (formattedCode: string) => `/a/${formattedCode}`,
  verify: '/verify',
  success: '/success',
  registrationStatus: '/status',
  adminHome: '/admin',
  adminLogin: '/admin/login',
  adminLoginVerify: '/admin/login/verify',
} as const;
